import DynamicScrollToTop from '@shared/ui/dynamic-scroll-to-top/ui/DynamicScrollToTop';
import React, { useEffect } from 'react';

import { useAtomValue } from 'jotai';
import { Container } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { CLOUDFRONT_LANDING_PAGE_ROOT_URL } from '@shared/config/constants/app';
import { useCheckUniqueApiSource } from '@shared/hooks/useCheckUniqueApiSource';
import { isRTL, redirectToStore } from '@shared/utils/general';
import {
  appendPurchaseImg,
  appendPurchaseScript,
  DC_LOCAL_API_SOURCE_NAME,
  ORDER_ID_DC_SS_NAME,
  PRICE_DC_SS_NAME,
  shouldRunDCEvent,
} from '@shared/utils/dc-events';

import styles from '../../styles/Thankyou.module.css';
import { ThankyouLIWelcomeTexts } from '@features/thank-you/ui/components/ThankyouLYWelcomeTexts';
import { ThankyouPTWelcomeTexts } from '@features/thank-you/ui/components/ThankyouPTWelcomeTexts';
import { isLY, isPT } from '@entities/country';
import { Footer } from '@shared/ui/footer';
import { Header } from '@shared/ui/header';
import { dcbAtom, languageAtom } from '@shared/store/slice-atoms';

const welcomeImg = `${CLOUDFRONT_LANDING_PAGE_ROOT_URL}/icons/welcome-img.webp`;

const ThankYou = ({
  activationURL,
  hideDownload,
  languageConfig = {},
  showOpenButton = false,
}) => {
  const { t } = useTranslation();

  const language = useAtomValue(languageAtom);
  const dcb = useAtomValue(dcbAtom);
  const { homeData, subscribeData, thankyouData } = dcb as any;

  const isPortugalWelcome =
    isPT(homeData.country) || isPT(thankyouData?.country) || language.locale === 'pt';
  const uniqueApiSource = useCheckUniqueApiSource();
  const isRTLDirection = isRTL(language.direction);
  const shouldShowOpenButton = showOpenButton && Boolean(activationURL);

  useEffect(() => {
    const apiSourceFromSessionStorage = sessionStorage.getItem(DC_LOCAL_API_SOURCE_NAME);
    const isInitiativeFlow = shouldRunDCEvent(apiSourceFromSessionStorage || homeData?.source);

    if (!isInitiativeFlow) return;

    const priceRevenueSS = sessionStorage.getItem(PRICE_DC_SS_NAME);
    const orderIdSS = sessionStorage.getItem(ORDER_ID_DC_SS_NAME);

    const priceRevenue = String(subscribeData?.price || priceRevenueSS);
    const orderId = String(subscribeData?.sessionId || orderIdSS);

    appendPurchaseScript(priceRevenue, orderId);
    appendPurchaseImg(priceRevenue, orderId);

    sessionStorage.removeItem(DC_LOCAL_API_SOURCE_NAME);
    sessionStorage.removeItem(PRICE_DC_SS_NAME);
    sessionStorage.removeItem(ORDER_ID_DC_SS_NAME);
  }, [homeData?.source]);

  if (isPortugalWelcome) {
    return (
      <article className="welcome-article">
        <DynamicScrollToTop />
        <ThankyouPTWelcomeTexts />
      </article>
    );
  }

  return (
    <article className="welcome-article">
      <DynamicScrollToTop />

      <Header languageConfig={languageConfig} />
      {uniqueApiSource ? (
        <section className="welcome-section-1">
          <div
            className="welcom-banner-uaeUnique"
            style={{
              backgroundImage: isRTLDirection
                ? 'url("https://d2rwz7a7lb9mw2.cloudfront.net/landingpage/banners/vipjourney_bg_ar.webp")'
                : 'url("https://d2rwz7a7lb9mw2.cloudfront.net/landingpage/banners/vipjourney_bg_en.webp")',
            }}
            onClick={redirectToStore}
          >
            <p
              className={styles.welcomText1UaeUnique}
              style={{ textAlign: isRTLDirection ? 'right' : 'left' }}
            >
              {t('welcome_to_vip')}
            </p>
            <p
              className={styles.welcomText2UaeUnique}
              style={{ textAlign: isRTLDirection ? 'right' : 'left' }}
            >
              {t('largest_libruary_access')}
            </p>
            <div className={styles.welcomButtonUaeUnique}>{t('download_vuz_app')}</div>
          </div>
          <div className="welcom-text-uaeUnique">
            <p className={styles.welcomText3UaeUnique}>{t('automatic_redirect')}</p>
            <p className={styles.welcomText4UaeUnique}>{t('manage_your_subscribtion')}</p>
            {shouldShowOpenButton ? (
              <a
                className={styles.openAppButton}
                href={activationURL}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('open_the_app')}
              </a>
            ) : null}
          </div>
        </section>
      ) : null}
      <Container className="container-sm mt-1">
        {!uniqueApiSource ? (
          <section className="welcome-section-1">
            {isLY(homeData.country) ? (
              <ThankyouLIWelcomeTexts />
            ) : (
              <div className="text-center">
                <div>
                  <img src={welcomeImg} className="img-fluid welcome-img" alt="" />
                  <p className="welcome-text-1">{t('welcome_1')}</p>
                  <p className="welcome-text-2">{t('welcome_2')}</p>
                  <p className="welcome-text-3 mt-1">{t('welcome_4')}</p>
                  <p className="welcome-text-3 mt-1 mb-1" style={{ whiteSpace: 'pre-line' }}>
                    {t('welcome_5')}
                  </p>
                  {shouldShowOpenButton ? (
                    <a
                      className={styles.openAppButton}
                      href={activationURL}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t('open_the_app')}
                    </a>
                  ) : null}
                </div>
              </div>
            )}
          </section>
        ) : null}

        {/* @ts-ignore */}
        <Footer hideFeatures activationURL={activationURL} isOTP={hideDownload} />
      </Container>
    </article>
  );
};

export default ThankYou;
